import React, { useState, useEffect, useRef } from 'react';
import { User, Award, TrendingUp, Calendar, CheckCircle, X, Camera, Loader2 } from 'lucide-react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

export const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [skillLevel, setSkillLevel] = useState('Principiante');
  const [handicap, setHandicap] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarAvailable, setCalendarAvailable] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        setName(response.data.name || '');
        setSkillLevel(response.data.skill_level || 'Principiante');
        setHandicap(response.data.handicap || '');
        setProfileImage(response.data.profile_image);
        setCalendarConnected(response.data.google_calendar_enabled || false);
      } catch (error) {
        console.error('Error loading profile');
      }
    };

    const checkCalendarStatus = async () => {
      try {
        const response = await api.get('/auth/google/status');
        setCalendarAvailable(response.data.configured);
      } catch (error) {
        setCalendarAvailable(false);
      }
    };

    fetchProfile();
    checkCalendarStatus();

    // Verificar si venimos del callback de Google
    const calendarParam = searchParams.get('calendar_connected');
    if (calendarParam === 'true') {
      toast.success('¡Google Calendar conectado exitosamente!');
      setCalendarConnected(true);
    } else if (calendarParam === 'false') {
      toast.error('Error al conectar Google Calendar');
    }
  }, [searchParams]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 5MB');
      return;
    }

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Formato no permitido. Usa JPG, PNG, WebP o GIF');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/user/profile/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileImage(response.data.image_url);
      toast.success('Foto de perfil actualizada');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/user/profile', {
        name,
        skill_level: skillLevel,
        handicap: handicap ? parseInt(handicap) : null,
      });
      toast.success('Perfil actualizado correctamente');
      refreshUser?.();
    } catch (error) {
      toast.error('Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const response = await api.get('/auth/google/connect');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('La integración con Google Calendar no está configurada aún.');
      } else {
        toast.error('Error al conectar con Google Calendar');
      }
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      await api.post('/auth/google/disconnect');
      setCalendarConnected(false);
      toast.success('Google Calendar desconectado');
    } catch (error) {
      toast.error('Error al desconectar Google Calendar');
    }
  };

  const getPlanLabel = (plan) => {
    const labels = { 'none': 'Sin Plan', 'inicial': 'Inicial', 'basic': 'Básico', 'socio': 'Socio' };
    return labels[plan] || plan;
  };

  return (
    <div className="min-h-screen py-20 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2">Mi Perfil</h1>
          <p className="text-muted-foreground">Administrá tu información personal</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Summary Card */}
          <Card className="glass-effect border-white/10">
            <CardContent className="pt-6 text-center">
              {/* Profile Image */}
              <div className="relative inline-block mb-4">
                <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center mx-auto">
                  {profileImage ? (
                    <img 
                      src={profileImage} 
                      alt="Perfil" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-primary" />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="absolute bottom-0 right-0 p-2 bg-primary rounded-full hover:bg-primary/90 transition-colors shadow-lg"
                  data-testid="change-photo-button"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
                  ) : (
                    <Camera className="h-4 w-4 text-primary-foreground" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              <h3 className="text-xl font-semibold mb-1">{name || user?.name}</h3>
              <p className="text-sm text-muted-foreground mb-2">{user?.email}</p>
              
              {/* Plan Badge */}
              <div className="inline-block px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium mb-4">
                Plan {getPlanLabel(user?.plan)}
              </div>

              <div className="flex items-center justify-center gap-4 text-sm">
                <div className="text-center">
                  <Award className="h-5 w-5 text-accent mx-auto mb-1" />
                  <p className="font-semibold">{skillLevel}</p>
                </div>
                {handicap && (
                  <div className="text-center">
                    <TrendingUp className="h-5 w-5 text-accent mx-auto mb-1" />
                    <p className="font-semibold">HCP {handicap}</p>
                  </div>
                )}
              </div>

              {/* Credits Display */}
              <div className="mt-4 p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-muted-foreground">Créditos disponibles</p>
                <p className="text-2xl font-bold text-primary">{user?.credits || 0}</p>
              </div>
            </CardContent>
          </Card>

          {/* Edit Profile Card */}
          <Card className="lg:col-span-2 glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="font-heading uppercase">Editar Perfil</CardTitle>
              <CardDescription>Actualizá tu información personal</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Nombre Completo</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 bg-white/5 border-white/10"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email}
                    disabled
                    className="mt-1 bg-white/5 border-white/10 opacity-50 cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">El email no puede ser modificado</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="skillLevel">Nivel de Habilidad</Label>
                    <Select value={skillLevel} onValueChange={setSkillLevel}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Principiante">Principiante</SelectItem>
                        <SelectItem value="Intermedio">Intermedio</SelectItem>
                        <SelectItem value="Avanzado">Avanzado</SelectItem>
                        <SelectItem value="Profesional">Profesional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="handicap">Handicap (Opcional)</Label>
                    <Input
                      id="handicap"
                      type="number"
                      value={handicap}
                      onChange={(e) => setHandicap(e.target.value)}
                      min="0"
                      max="54"
                      className="mt-1 bg-white/5 border-white/10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                >
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Google Calendar Card */}
          <Card className="lg:col-span-3 glass-effect border-white/10">
            <CardHeader>
              <CardTitle className="font-heading uppercase flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Integración con Google Calendar
              </CardTitle>
              <CardDescription>
                Sincroniza automáticamente tus reservas con tu calendario de Google
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!calendarAvailable ? (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-yellow-500 text-xl">⚠️</span>
                    <div>
                      <p className="font-semibold text-yellow-500">Funcionalidad no disponible</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        La integración con Google Calendar aún no está configurada.
                      </p>
                    </div>
                  </div>
                </div>
              ) : calendarConnected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold">Google Calendar Conectado</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Todas tus reservas se agregarán automáticamente
                      </p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleDisconnectCalendar}
                    variant="outline"
                    className="w-full border-white/20 hover:bg-white/5"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Desconectar Google Calendar
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-3">
                      Conecta tu cuenta de Google para:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>Sincronizar automáticamente tus reservas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>Recibir recordatorios antes de cada sesión</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={handleConnectCalendar}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Conectar con Google Calendar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
